import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFloppyDisk,
  faList,
  faPlus,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { hrToolsService } from '../../services/hrToolsService'
import {
  DEFAULT_EEAP_PREAPPROVED_CATEGORIES,
  MAX_EEAP_ITEM_CATEGORY_LENGTH,
  MAX_EEAP_PREAPPROVED_CATEGORIES,
} from '../../utils/eeapPreapprovedCategories'

const HrEeapCategoriesCard: React.FC = () => {
  const [draft, setDraft] = useState<string[]>([...DEFAULT_EEAP_PREAPPROVED_CATEGORIES])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const next = await hrToolsService.getEeapCategories()
        if (!cancelled) setDraft(next.length ? next : [...DEFAULT_EEAP_PREAPPROVED_CATEGORIES])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load items')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const patchAt = (index: number, value: string) => {
    setDraft((prev) => prev.map((item, i) => (i === index ? value : item)))
    setMessage(null)
  }

  const removeAt = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index))
    setMessage(null)
  }

  const addRow = () => {
    setDraft((prev) => (prev.length >= MAX_EEAP_PREAPPROVED_CATEGORIES ? prev : [...prev, '']))
    setMessage(null)
  }

  const onSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const next = await hrToolsService.saveEeapCategories(draft)
      setDraft(next)
      setMessage('Saved.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faList} />
          Items
        </h2>
        <p className="text-sm text-base-content/70">
          Employees see this list when they request Work Related Equipment. HR can optionally tag a
          ledger transaction with an item so you can sort later — for example EEAP plus Hard Hat.
          Existing transactions keep whatever value they already have.
        </p>

        {error ? <div className="alert alert-error mt-2"><span>{error}</span></div> : null}
        {message ? <div className="alert alert-success mt-2"><span>{message}</span></div> : null}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-base-content/70">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading items…
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {draft.length === 0 ? (
              <p className="text-sm text-base-content/60">Add at least one item.</p>
            ) : (
              <div className="space-y-2">
                {draft.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      className="input input-bordered w-full"
                      value={value}
                      maxLength={MAX_EEAP_ITEM_CATEGORY_LENGTH}
                      onChange={(e) => patchAt(index, e.target.value)}
                      placeholder="Item name"
                      aria-label={`Item ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-square text-error"
                      onClick={() => removeAt(index)}
                      aria-label="Remove"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn btn-outline"
                disabled={draft.length >= MAX_EEAP_PREAPPROVED_CATEGORIES}
                onClick={addRow}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add item
              </button>
              <button
                type="button"
                className="btn btn-primary gap-2"
                disabled={saving}
                onClick={() => void onSave()}
              >
                <FontAwesomeIcon
                  icon={saving ? faSpinner : faFloppyDisk}
                  className={saving ? 'animate-spin' : ''}
                />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HrEeapCategoriesCard
